import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ArrowRight } from 'lucide-react';

import { useTranslation } from 'react-i18next';

/**
 * DidYouKnowHomeCard — top-of-feature-list highlight card.
 *
 * Renders nothing until we know there is at least one published fact.
 * When the user has NOT yet read the current fact, the card displays
 * a pulsing animated border + a "NEW" badge to draw attention.
 */
export default function DidYouKnowHomeCard() {
  const {
    t,
    i18n
  } = useTranslation();

  const navigate = useNavigate();
  const [fact, setFact] = useState(null);
  const [loading, setLoading] = useState(true);
  const lang = (i18n.language || 'en').split('-')[0];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const apiBase = `${window.location.origin}/api`;
        const res = await axios.get(`${apiBase}/did-you-know/current`, {
          params: { locale: lang },
          withCredentials: true,
          timeout: 4000,
        });
        if (!cancelled) setFact(res.data?.fact || null);
      } catch {
        // Silent — user-facing absence is acceptable.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lang]);

  if (loading || !fact) return null;
  const isNew = !fact.is_read;

  return (
    <div
      className={`relative rounded-2xl ${isNew ? 'dyk-pulse-frame' : ''}`}
      data-testid="dyk-home-card-wrapper"
    >
      <button
        onClick={() => navigate('/did-you-know')}
        className="w-full text-left rounded-2xl p-6 transition-all hover:scale-[1.01] active:scale-[0.99] group relative overflow-hidden"
        style={{
          background: 'var(--glass-bg)',
          border: '1px solid var(--glass-border)',
          backdropFilter: 'blur(12px)',
        }}
        data-testid="home-did-you-know-card"
      >
        <div className="flex items-center justify-between mb-3">
          <div
            className="p-2.5 rounded-xl inline-flex"
            style={{ background: 'var(--glass-bg-active)' }}
          >
            <Sparkles className="h-5 w-5" style={{ color: 'var(--theme-accent)' }} />
          </div>
          {isNew && (
            <span
              className="dyk-new-badge text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-bold"
              data-testid="dyk-new-badge"
            >
              {t('generated.didyouknowhomecard_new_367f')}
            </span>
          )}
        </div>
        <h2
          className="text-lg font-semibold mb-1"
          style={{ color: 'var(--theme-text)' }}
          data-testid="dyk-card-section-title"
        >
          {t('generated.didyouknowhomecard_did_you_know_a4ac')}
        </h2>
        <p
          className="text-sm font-medium mb-2 line-clamp-2"
          style={{ color: 'var(--theme-text)' }}
          data-testid="dyk-card-fact-title"
        >
          {fact.title}
        </p>
        <p
          className="text-xs leading-relaxed mb-4 line-clamp-2"
          style={{ color: 'var(--theme-textSecondary)' }}
          data-testid="dyk-card-fact-preview"
        >
          {fact.content}
        </p>
        <div
          className="flex items-center gap-1.5 text-sm font-medium transition-all group-hover:gap-2.5"
          style={{ color: 'var(--theme-accent)' }}
        >
          {t('generated.didyouknowhomecard_read_more_c8e0')}
          <ArrowRight className="h-4 w-4" />
        </div>
      </button>
    </div>
  );
}
